import { Injectable, inject } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ERROR_MESSAGES } from '../constants/app.constant';

@Injectable({
    providedIn: 'root'
})
export class ToastMessageService {
    private readonly nzMessageService = inject(NzMessageService);

    /**
     * Shows an error message. If the error code is > 400, shows a generic error message.
     * Otherwise, shows the specific error message.
     * 
     * @param error - The error object or message
     * @param options - Optional NzMessageService options
     */
    error(error: any, options?: { nzDuration?: number }): void {

        const errorCode = this.extractErrorCode(error);

        if (errorCode > 400) {
            this.nzMessageService.error(ERROR_MESSAGES.COMMON, options);
        } else {
            const errorMessage = this.extractErrorMessage(error);
            this.nzMessageService.error(errorMessage, options);
        }
    }

    /**
     * Shows a success message
     */
    success(message: string, options?: { nzDuration?: number }): void {
        this.nzMessageService.success(message, options);
    }

    /**
     * Shows a warning message
     */
    warning(message: string, options?: { nzDuration?: number }): void {
        this.nzMessageService.warning(message, options);
    }

    /**
     * Shows an info message
     */
    info(message: string, options?: { nzDuration?: number }): void {
        this.nzMessageService.info(message, options);
    }

    /**
     * Extracts the error code from various error formats
     */
    private extractErrorCode(error: any): number {
        if (typeof error === 'object' && error !== null) {
            // Check for direct error code properties
            const directCode = error?.code || error?.status || error?.statusCode || error?.error?.code || error?.error?.status;
            if (directCode) {
                return directCode;
            }
            
            // Check if the error message contains HTTP status code
            // Pattern: "Http failure response for ... XXX Error Message"
            const message = error?.message || '';
            const statusCodeMatch = message.match(/:\s+(\d{3})\s+/);
            if (statusCodeMatch && statusCodeMatch[1]) {
                return parseInt(statusCodeMatch[1], 10);
            }
        }
        return 0;
    }

    /**
     * Extracts the error message from various error formats
     */
    private extractErrorMessage(error: any): string {
        if (typeof error === 'string') {
            return error;
        }

        if (typeof error === 'object' && error !== null) {
            return (
                error?.message ||
                error?.error?.message ||
                error?.statusText ||
                error?.error?.statusText ||
                String(error)
            );
        }

        return 'An error occurred';
    }
}
